import type { AutoFormInputComponentProps } from "../types"
import { useTranslations } from "next-intl"
import { useEffect, useMemo } from "react"
import { useFormContext } from "react-hook-form"
import { FormControl, FormItem, FormMessage } from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { cn } from "@/lib/utils"
import AutoFormLabel from "../common/label"
import AutoFormTooltip from "../common/tooltip"

type MedalCountConfigType = {
  [medalCode: string]: number
}

type MedalInfo = {
  code: string
  name: string
  color: string
}

type MedalCountInputProps = {
  maxTotalMedals?: number
}

const MEDAL_INFO: Record<string, MedalInfo> = {
  GOLD: { code: "GOLD", name: "GOLD", color: "text-yellow-600" },
  SILVER: { code: "SILVER", name: "SILVER", color: "text-gray-500" },
  BRONZE: { code: "BRONZE", name: "BRONZE", color: "text-amber-700" },
}

export function AutoFormMedalCountConfig({
  label,
  isRequired,
  field,
  fieldConfigItem,
}: AutoFormInputComponentProps) {
  const { watch } = useFormContext()
  const t = useTranslations("auto_form.medal_count_config")
  const tGeneric = useTranslations("generic")
  // Observar los valores de medals y medalCodes - usar watch directamente para reactividad inmediata
  const medalsEnabled = watch("medals") as boolean
  const medalCodesRaw = watch("medalCodes") as string[] | undefined

  const selectedMedalCodes = useMemo(() => {
    return medalCodesRaw || []
  }, [medalCodesRaw])

  const maxMedalsPerCategory = 10
  // Obtener el límite máximo total desde inputProps
  const inputProps = fieldConfigItem?.inputProps as MedalCountInputProps | undefined
  const maxTotalMedals = inputProps?.maxTotalMedals || 20

  // Determinar si el componente debe ser visible
  const isVisible = useMemo(() => {
    return medalsEnabled && selectedMedalCodes.length > 0
  }, [medalsEnabled, selectedMedalCodes])

  // Obtener configuración actual o inicializar
  const currentConfig = useMemo(() => {
    const fieldValue = field.value as MedalCountConfigType
    // Asegurar que config sea un objeto válido
    const config = (fieldValue && typeof fieldValue === "object" && !Array.isArray(fieldValue))
      ? fieldValue
      : {}

    // Asegurar que todos los códigos seleccionados tengan una entrada
    const newConfig: MedalCountConfigType = {}
    selectedMedalCodes.forEach((code) => {
      newConfig[code] = config[code] ?? 1 // Usar valor existente o por defecto
    })

    return newConfig
  }, [field.value, selectedMedalCodes])

  // Calcular el total actual de medallas
  const currentTotalMedals = useMemo(() => {
    return Object.values(currentConfig).reduce((sum, count) => sum + (count || 0), 0)
  }, [currentConfig])

  // Sincronizar cambios iniciales para evitar inconsistencias
  useEffect(() => {
    if (isVisible && selectedMedalCodes.length > 0) {
      const fieldValue = field.value as MedalCountConfigType
      const currentFieldValue = fieldValue && typeof fieldValue === "object" && !Array.isArray(fieldValue)
        ? fieldValue
        : {}

      // Verificar si necesitamos sincronizar
      let needsSync = false
      const newConfig: MedalCountConfigType = { ...currentFieldValue }

      // Verificar códigos faltantes o valores inválidos
      for (const code of selectedMedalCodes) {
        if (currentFieldValue[code] === undefined || currentFieldValue[code] === null) {
          newConfig[code] = 1
          needsSync = true
        }
      }

      // Solo actualizar si hay cambios necesarios
      if (needsSync) {
        field.onChange(newConfig)
      }
    }
  }, [isVisible, selectedMedalCodes, field])

  // Efecto adicional para sincronizar cuando cambien los códigos de medalla seleccionados
  useEffect(() => {
    if (selectedMedalCodes.length > 0 && isVisible) {
      const fieldValue = field.value as MedalCountConfigType
      const currentFieldValue = fieldValue && typeof fieldValue === "object" && !Array.isArray(fieldValue)
        ? fieldValue
        : {}

      // Crear configuración con todos los códigos necesarios
      const completeConfig: MedalCountConfigType = {}
      let hasChanges = false

      selectedMedalCodes.forEach((code) => {
        if (currentFieldValue[code] !== undefined && typeof currentFieldValue[code] === "number") {
          completeConfig[code] = currentFieldValue[code]
        }
        else {
          completeConfig[code] = 1
          hasChanges = true
        }
      })

      // Solo actualizar si hay cambios
      if (hasChanges || Object.keys(currentFieldValue).length !== selectedMedalCodes.length) {
        field.onChange(completeConfig)
      }
    }
  }, [selectedMedalCodes, isVisible, field])

  // Actualizar el valor del campo cuando cambie la configuración
  const handleCountChange = (medalCode: string, value: string) => {
    // Convertir el valor string a número
    const count = Number.parseInt(value, 10)
    // Asegurar que count sea un número válido, permitir temporalmente valores vacíos o 0
    const validCount = value === "" ? "" : (Number.isInteger(count) && count >= 0 ? count : 1)

    // Si el valor está vacío, mantenerlo temporalmente
    if (value === "") {
      const newConfig: MedalCountConfigType = {
        ...currentConfig,
        [medalCode]: 1, // Mantener un valor válido internamente
      }
      field.onChange(newConfig)
      return
    }

    const requestedCount = Number(validCount)

    // Calcular el total actual sin incluir el valor del campo que se está modificando
    const otherMedalsTotal = Object.entries(currentConfig)
      .filter(([code]) => code !== medalCode)
      .reduce((sum, [, count]) => sum + (count || 0), 0)

    // Calcular el máximo permitido para este campo específico
    const maxAllowedForThisField = Math.max(1, maxTotalMedals - otherMedalsTotal)

    // Aplicar límites: mínimo 1, máximo por categoría, y no exceder el límite total
    const clampedCount = Math.max(1, Math.min(requestedCount, maxMedalsPerCategory, maxAllowedForThisField))

    const newConfig: MedalCountConfigType = {
      ...currentConfig,
      [medalCode]: clampedCount,
    }
    field.onChange(newConfig)
  }

  // Si no es visible, retornar null
  if (!isVisible) {
    return null
  }

  return (
    <div className={cn(fieldConfigItem?.containerClassName)}>
      <FormItem>
        <AutoFormLabel
          isRequired={isRequired}
          label={fieldConfigItem?.label || label || t("title")}
        />
        <FormControl>
          <div className="space-y-3 rounded-lg border bg-muted/30 p-4">
            <div className="mb-3 text-sm text-muted-foreground">
              {t("description")}
            </div>
            <div className="mb-3 flex items-center justify-between text-sm">
              <span className="text-muted-foreground">
                {t("medal_total")}
              </span>
              <span className={cn("font-medium", {
                "text-red-600": currentTotalMedals > maxTotalMedals,
                "text-amber-600": currentTotalMedals === maxTotalMedals,
                "text-green-600": currentTotalMedals < maxTotalMedals,
              })}
              >
                {currentTotalMedals}
                {tGeneric("diagonal")}
                {maxTotalMedals}
              </span>
            </div>
            {selectedMedalCodes.map((medalCode) => {
              const medalInfo = MEDAL_INFO[medalCode] || {
                code: medalCode,
                name: medalCode,
                color: "text-gray-600",
              }

              // Obtener el nombre traducido de la medalla
              const translatedName = medalCode.toLowerCase() === "gold"
                ? t("medals.gold")
                : medalCode.toLowerCase() === "silver"
                  ? t("medals.silver")
                  : medalCode.toLowerCase() === "bronze"
                    ? t("medals.bronze")
                    : medalCode

              // Asegurar que currentCount sea un número válido
              const rawCount = currentConfig[medalCode]
              const currentCount = typeof rawCount === "number" ? rawCount : 1

              // Calcular el máximo permitido para este campo específico
              const otherMedalsTotal = Object.entries(currentConfig)
                .filter(([code]) => code !== medalCode)
                .reduce((sum, [, count]) => sum + (count || 0), 0)
              const maxAllowedForThisField = Math.max(1, maxTotalMedals - otherMedalsTotal)

              return (
                <div key={medalCode} className="flex items-center gap-3">
                  <div className="flex min-w-[120px] items-center gap-2">
                    <div className={cn("w-3 h-3 rounded-full", {
                      "bg-yellow-400": medalCode === "GOLD",
                      "bg-gray-400": medalCode === "SILVER",
                      "bg-amber-600": medalCode === "BRONZE",
                      "bg-gray-300": !["GOLD", "SILVER", "BRONZE"].includes(medalCode),
                    })}
                    />
                    <Label className={cn("font-medium", medalInfo.color)}>
                      {translatedName}
                    </Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Input
                      key={`medal-count-${medalCode}-${String(field.name || "default")}`}
                      className="w-20 text-center"
                      max={Math.min(maxMedalsPerCategory, maxAllowedForThisField)}
                      min={1}
                      onChange={e => handleCountChange(medalCode, e.target.value)}
                      type="number"
                      value={currentCount.toString()}
                    />
                    <span className="text-xs text-muted-foreground">
                      {tGeneric("open_parenthesis")}
                      {tGeneric("max")}
                      {Math.min(maxMedalsPerCategory, maxAllowedForThisField)}
                      {tGeneric("close_parenthesis")}
                    </span>
                  </div>
                </div>
              )
            })}
            {selectedMedalCodes.length === 0 && (
              <div className="py-2 text-center text-sm text-muted-foreground">
                {t("no_medals_selected")}
              </div>
            )}
          </div>
        </FormControl>
        <AutoFormTooltip fieldConfigItem={fieldConfigItem} />
        <FormMessage />
      </FormItem>
    </div>
  )
}
